// Helper functions for order processing

export const getOrderStatus = (bookingInfo, bookingResolved) => {
    if (bookingResolved) return 'Completed';
    
    // Handle new cart_data structure - check has_customer_info
    if (bookingInfo?.summary?.has_customer_info !== undefined) {
        return bookingInfo.summary.has_customer_info ? 'Confirmed' : 'Incomplete';
    }
    
    // Handle legacy booking_info structure
    if (bookingInfo?.booking_status?.status) {
        const status = bookingInfo.booking_status.status;
        let readableStatus = status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        // Normalize 'Confirmed' to 'Booking Confirmed' to handle backend data inconsistency
        if (readableStatus === 'Confirmed') {
            return 'Booking Confirmed';
        }
        return readableStatus;
    }
    return 'Pending';
};

export const getOrderStatusColor = (status, bookingResolved) => {
    if (bookingResolved) return 'text-green-500 tag-secondary';
    const statusLower = status?.toLowerCase();
    if (statusLower === 'confirmed') {
        return 'text-green-500 tag-green';
    }
    if (statusLower === 'incomplete') {
        return 'text-yellow-600 tag-orange';
    }
    if (statusLower?.includes('confirmed') || statusLower?.includes('completed')) {
        return 'text-secondary tag-green';
    }
    if (statusLower?.includes('pending') || statusLower?.includes('processing')) {
        return 'text-yellow-600 tag-orange';
    }
    if (statusLower?.includes('cancelled') || statusLower?.includes('rejected')) {
        return 'text-error tag-red';
    }
    return 'text-font-secondary tag-secondary';
};

// Unified status styling function that combines color classes and icon
export const getOrderStatusStyles = (status, bookingResolved) => {
    if (bookingResolved) {
        return { 
            textColor: 'text-green-500', 
            icon: 'material-symbols:check-circle-outline',
            className: 'text-green-500 tag-secondary'
        };
    }
    
    const statusLower = status?.toLowerCase();
    
    if (statusLower === 'confirmed') {
        return { 
            textColor: 'text-green-500', 
            icon: 'material-symbols:check-circle-outline',
            className: 'text-green-500 tag-green'
        };
    }
    
    if (statusLower === 'incomplete') {
        return { 
            textColor: 'text-yellow-500', 
            icon: 'carbon:incomplete-error',
            className: 'text-yellow-600 tag-orange'
        };
    }
    
    if (statusLower?.includes('confirmed') || statusLower?.includes('completed')) {
        return { 
            textColor: 'text-green-500', 
            icon: 'material-symbols:check-circle-outline',
            className: 'text-secondary tag-green'
        };
    }
    
    if (statusLower?.includes('pending') || statusLower?.includes('processing')) {
        return { 
            textColor: 'text-yellow-500', 
            icon: 'carbon:incomplete-error',
            className: 'text-yellow-600 tag-orange'
        };
    }
    
    if (statusLower?.includes('cancelled') || statusLower?.includes('rejected')) {
        return { 
            textColor: 'text-error', 
            icon: 'material-symbols:cancel-outline',
            className: 'text-error tag-red'
        };
    }
    
    return { 
        textColor: 'text-font-secondary', 
        icon: 'ph:question',
        className: 'text-font-secondary tag-secondary'
    };
};

export const getTotalAmount = (bookingInfo) => {
    // Handle new cart_data structure
    if (bookingInfo?.full_cart?.items?.length > 0) {
        const totalRentalCost = bookingInfo.full_cart.items.reduce((sum, item) => sum + (item.rental_cost || 0), 0);
        const totalDeliveryCost = bookingInfo.full_cart.items.reduce((sum, item) => sum + (item.delivery_cost || 0), 0);
        const total = totalRentalCost + totalDeliveryCost;
        return total > 0 ? `$${total}` : 'Quote Pending';
    }
    
    // Handle legacy booking_info structure
    if (bookingInfo?.pricing_info?.total_estimate) {
        return bookingInfo.pricing_info.total_estimate;
    }
    if (bookingInfo?.pricing_info?.rental_rate) {
        return bookingInfo.pricing_info.rental_rate;
    }
    if (bookingInfo?.pricing_info?.total_amount) {
        return `$${bookingInfo.pricing_info.total_amount}`;
    }
    return 'Quote Pending';
};

export const getEquipmentList = (bookingInfo) => {
    // Handle new cart_data structure
    if (bookingInfo?.full_cart?.items?.length > 0) {
        return bookingInfo.full_cart.items
            .map(item => `${item.name}${item.model ? ` (${item.model})` : ''}`)
            .join(', ');
    }
    // Handle legacy booking_info structure
    if (bookingInfo?.equipment_requested?.length > 0) {
        return bookingInfo.equipment_requested
            .map(equipment => `${equipment.type}${equipment.model ? ` (${equipment.model})` : ''}`)
            .join(', ');
    }
    return 'Equipment details not specified';
};

// Review status styling function
export const getReviewStatusStyles = (status) => {
    if (status === "Reviewed") {
        return {
            icon: 'material-symbols:check-circle',
            className: 'text-green-500 text-xl'
        };
    }
    // Default to "Open for Review"
    return {
        icon: 'material-symbols:radio-button-unchecked',
        className: 'text-font-secondary text-xl'
    };
};